import { useQuery } from "@hooks/useQuery";
import { Pagination, TableColumnsType, Typography } from "antd";
import { get as _get } from "lodash";
import { useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { DataTable } from "~/Components";
import ConfirmationModal from "~/Components/Modal/confirmation-modal";
import { TableDrawer } from "~/Components/TableDrawer/TableDrawer";
import { useLocalStorage } from "~/Hooks/useLocalStorage";
import { useNotification } from "~/Hooks/useNotification";
import { passengerCMSApi, post, remove } from "~/Services";
import { passengerHost } from "~/Utils";
import ActionDropdown, { TypeData } from "~/Utils/ActionDropdown";
import { decodeHTML } from "~/Utils/CommonUtils";
import { drilldownData, ModuleConfig } from "~/Utils/drilldownData";
import { passengerAirportColumn, renderActionsColumn, renderDateColumn } from "~/Utils/TableRenderFunction";
import { useSubCategoriesDocumentDetails } from "./youtube.hooks";

export const Youtube = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [id, setId] = useState<string | undefined>(selectedRowData?.id);

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);

  const [searchValue, setSearchValue] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [selectedPublished, setSelectedPublished] = useState<any>("");

  const publishedData: TypeData[] = [
    { label: "Yes", value: true },
    { label: "No", value: false },
  ];

  const [data, setData] = useState<any[]>([]);
  const [sortedData, setSortedData] = useState<any[]>([]);
  const currentPageData = sortedData.slice(currentPage * pageSize, (currentPage + 1) * pageSize);
  const [sortOrder, setSortOrder] = useState<string | undefined>();
  const [orderByName, setOrderByName] = useState<string>("id");
  const [currentAirport] = useLocalStorage("currentAirport", null);

  const {
    data: drilldown,
    isLoading: drilldownLoading,
    refetch: refetchDocument,
  } = useSubCategoriesDocumentDetails(id || "");
  useEffect(() => {
    if (id) refetchDocument();
  }, [id]);

  useEffect(() => {
    if (query.get("activeTab") === "youtube") {
      setSelectedPublished("");
      setSearchValue("");
      setCurrentPage(0);
    }
  }, [query.get("activeTab")]);

  useEffect(() => {
    let filteredData = [...data];

    // 🔍 Filter by search
    if (searchValue) {
      const searchLower = decodeHTML(searchValue).toLowerCase();
      filteredData = filteredData.filter((item: any) =>
        decodeHTML(item.title || "")
          .toLowerCase()
          .includes(searchLower)
      );
    }

    // ✅ Filter by Published
    if (selectedPublished !== "") {
      filteredData = filteredData.filter((item: any) => item.published === selectedPublished);
    }

    setSortedData(filteredData);
    setTotalRecords(filteredData.length);
    setCurrentPage(0);
  }, [data, searchValue, selectedPublished]);

  const getRecords = async () => {
    setIsLoading(true);
    try {
      const url = `/api/get-youtube-details/all-fields`;
      const res = await passengerCMSApi.get(url);

      let items = res?.data;
      if (currentAirport) {
        items = items.filter((item: any) => {
          if (!item.airports) return false;

          const airportList = Array.isArray(item.airports)
            ? item.airports.map((a: any) => a.trim().toLowerCase())
            : item.airports.split(",").map((a: string) => a.trim().toLowerCase());

          return airportList.includes(currentAirport.toLowerCase());
        });
      }
      setData(items);
      setSortedData(items);
      setTotalRecords(items.length);
    } catch (err) {
      console.error("Failed to fetch YouTube records", err);
      setData([]);
      setSortedData([]);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getRecords();
  }, []);

  const handleDelete = async (id: string) => {
    remove(`${passengerHost}/api/homepage-banners/${id}`)
      .then(() => getRecords())
      .catch(() => {});
  };

  const columns: TableColumnsType<any> = [
    {
      title: "Sequence",
      dataIndex: "sequence",
      key: "sequence",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "sequence"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "sequence"
            ? "descend"
            : undefined,
    },
    {
      title: "Thumbnail",
      dataIndex: "thumbnail_img",
      key: "thumbnail_img",
      render: (img: string) =>
        img ? (
          <img
            src={img}
            alt="thumbnail"
            className="w-24 h-16 object-cover rounded-md border"
          />
        ) : (
          "-"
        ),
    },
    {
      title: "Title",
      dataIndex: "thumnail_text_english",
      key: "thumnail_text_english",
      render: (text: string) => decodeHTML(text),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "thumnail_text_english"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "thumnail_text_english"
            ? "descend"
            : undefined,
    },
    // {
    //   title: "Video Link",
    //   dataIndex: "video_link",
    //   key: "video_link",
    //   render: (link: string) =>
    //     link ? (
    //       <a
    //         href={link}
    //         target="_blank"
    //         rel="noreferrer"
    //         className="text-blue-600 underline"
    //       >
    //         Watch
    //       </a>
    //     ) : (
    //       "-"
    //     ),
    // },
    {
      title: "Duration",
      dataIndex: "duration",
      key: "duration",
      render: (text: string) => text || "-",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "duration"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "duration"
            ? "descend"
            : undefined,
    },
    {
      title: "Airports",
      dataIndex: "airports",
      key: "airports",
      render: (text) => passengerAirportColumn(text),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "airports"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "airports"
            ? "descend"
            : undefined,
    },
    // {
    //   title: "Redirect URL",
    //   dataIndex: "redirectedURL",
    //   key: "redirectedURL",
    //   render: (url: string) =>
    //     url ? (
    //       <a
    //         href={url.startsWith("http") ? url : `https://${url}`}
    //         target="_blank"
    //         rel="noreferrer"
    //         className="text-blue-600 underline"
    //       >
    //         {url}
    //       </a>
    //     ) : (
    //       "-"
    //     ),
    // },
    {
      title: "Published",
      dataIndex: "published",
      key: "published",
      render: (value: boolean) => (value ? "Yes" : "No"),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "published"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "published"
            ? "descend"
            : undefined,
    },
    // {
    //   title: "Sequence",
    //   dataIndex: "sequence",
    //   key: "sequence",
    //   sorter: true,
    // },
    {
      title: "Created At",
      dataIndex: "created",
      key: "created",
      render: (value: string) => renderDateColumn(value),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "created"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "created"
            ? "descend"
            : undefined,
    },
    {
      title: "Updated At",
      dataIndex: "changed",
      key: "changed",
      render: (value: any, record: any) => {
        if (value && record.created && value === record.created) {
          return "-";
        }
        return renderDateColumn(value);
      },
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "changed"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "changed"
            ? "descend"
            : undefined,
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      fixed: "right",
      render: (_: any, record: any) =>
        renderActionsColumn(
          record,
          setSelectedRowData,
          setDrawerVisible,
          (e: any) => {
            e.preventDefault();
            navigate(`/dedicated/passenger/homepage/youtube/add?editId=${record.id}`);
          },
          undefined,
          () => {
            setSelectedRowData(record);
            setDeleteModalVisible(true);
          },
          "users"
        ),
    },
  ];

  useEffect(() => {
    setId(selectedRowData?.id);
  }, [selectedRowData]);

  const handleTableSort = (column: any, sortOrder: string) => {
    let order = "";
    if (sortOrder === "asc" && orderByName === column.dataIndex) {
      setSortOrder("desc");
      order = "desc";
    } else {
      setSortOrder("asc");
      order = "asc";
    }

    setOrderByName(column.dataIndex);
    const tempData = [...sortedData];
    setSortedData(tempData.sort((a: any, b: any) => handleSort(a, b, order, column.dataIndex)));
    setCurrentPage(0);
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    if (apiUrl === "") return;
    const payload = {
      action,
      method,
      module: "Home Page",
      subModule: "youtube",
      type: "DEDICATED",
      application: "PASSENGER_ECMS",
      airportName: currentAirport,
      reviewNotes,
      appList: [{ appName: "PASSENGER_ECMS", apiUrl }],
      payload: data,
      status,
    };
    post("/management/publish", payload)
      .then((response: any) => {
        openToast({
          content: "Data Sent for Review",
          type: "success",
        });
        if (response.status === "OK" && status == "PENDING") {
          navigate("/dedicated/status-information?activeTab=pending");
        }
      })
      .catch(() => openToast({ content: "Failed to send data", type: "error" }));
  };

  async function sendDeleteRequest(reviewNotes: string) {
    try {
      await onSubmit(
        selectedRowData,
        `${passengerHost}/api/youtube-video-crud-api-data/${selectedRowData?.id}`,
        "DELETE",
        "DELETE",
        "PENDING",
        reviewNotes
      );
    } catch {}
  }

  const handlePublishedChange = (value: any) => {
    setSelectedPublished(value);
    setCurrentPage(0);
  };

  return (
    <>
      <DataTable
        tabOffset={60}
        columns={columns.map((col) => ({
          ...col,
          onHeaderCell: (column: any) => (col.sorter ? { onClick: () => handleTableSort(col, sortOrder) } : {}),
        }))}
        multiSelect={false}
        className="table-clickable"
        tableData={
          currentPageData
            ? currentPageData.map((d: any) => ({
                ...d,
                name: decodeHTML(d.name || ""),
              }))
            : []
        }
        isLoading={isLoading}
        exportFileName="YOUTUBE"
        isPagination={true}
        recordsPerPage={10}
        onSearchChange={(searchText: string) => {
          setSearchValue(searchText);
          setCurrentPage(0);
        }}
        hideExport={true}
        isServerSearchApi={false}
        searchPlaceholderText={"Search Title"}
        onClick={(record) => setSelectedRowData(record)}
        additionalCTA={
          <div className="flex items-center space-x-2">
            <ActionDropdown
              value={selectedPublished}
              options={publishedData}
              onChange={handlePublishedChange}
              placeholder="Select Published"
              className="w-48"
            />
            {/* <ActionDropdown
              value={selectedCategoriesData}
              options={categoriesData}
              onChange={handleCategoriesChange}
              placeholder="Select Categories"
              className="w-48"
            /> */}
          </div>
        }
      />

      <div className="pagination">
        <Pagination
          defaultCurrent={1}
          current={currentPage + 1}
          total={totalRecords}
          pageSize={pageSize}
          onChange={(curPage, newPageSize) => {
            setCurrentPage(curPage - 1);
            if (newPageSize !== pageSize) {
              setPageSize(newPageSize);
              setCurrentPage(0);
            }
          }}
          showSizeChanger
          pageSizeOptions={["10", "20", "50", "100"]}
        />
      </div>

      <TableDrawer
        title={
          <Typography.Title
            level={4}
            className="!mb-0"
          >
            Detailed View
          </Typography.Title>
        }
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        fields={drilldownData[_get(drilldown, "subModule", "youtube") as ModuleConfig]}
        data={drilldown}
      />

      <ConfirmationModal
        type="delete"
        open={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onSubmit={sendDeleteRequest}
        defaultReviewNotes="Kindly review this Homepage"
      />
    </>
  );
};

export function handleSort(a: any, b: any, order: string | undefined, sortBy: string) {
  if (order === "desc") return a[sortBy] < b[sortBy] ? 1 : -1;
  if (order === "asc") return a[sortBy] > b[sortBy] ? 1 : -1;
  return 0;
}

export default Youtube;
